import type {
  EmbeddingRoute,
  LlmApiStyle,
  LlmProvider,
  LlmRoute,
  LlmSettings,
} from "./llm";
import type { MainPromptPreset } from "./presetAssembler";

export const FEATURE_IDS = [
  "core-chat",
  "evolution",
  "evolution-entry",
  "evolution-item",
  "evolution-player",
  "evolution-npc",
  "evolution-monster",
  "evolution-misc",
  "deep-summary",
  "evolution-bio",
  "narrative-memory",
  "rag-query",
  "rag-embedding",
  "crafting",
  "branch-options",
  "quick-chat",
  "quick-chat-group",
  "plot-evolution",
] as const;

export type FeatureId = (typeof FEATURE_IDS)[number];
export type EndpointKind = "generation" | "embedding";

export const FEATURE_LABELS: Record<FeatureId, string> = {
  "core-chat": "主叙事",
  evolution: "并发演化统一路由",
  "evolution-entry": "演化：登场判断",
  "evolution-item": "演化：物品",
  "evolution-player": "演化：玩家",
  "evolution-npc": "演化：NPC",
  "evolution-monster": "演化：怪物",
  "evolution-misc": "演化：杂项",
  "deep-summary": "深度总结",
  "evolution-bio": "人物传记压缩",
  "narrative-memory": "叙事记忆",
  "rag-query": "RAG 查询改写",
  "rag-embedding": "RAG Embedding",
  crafting: "自定义锻造文案",
  "branch-options": "分支选项",
  "quick-chat": "现场快聊：单聊",
  "quick-chat-group": "现场快聊：群聊",
  "plot-evolution": "剧情演化",
};

export interface EndpointCombo {
  id: string;
  name: string;
  kind: EndpointKind;
  enabled: boolean;
  baseUrl: string;
  model: string;
  useProxy: boolean;
  provider?: LlmProvider;
  apiStyle?: LlmApiStyle;
  temperature?: number;
  maxTokens?: number;
  timeoutMs?: number;
  retries?: number;
  dimensions?: number;
}

export type FeatureRouteMap = Partial<Record<FeatureId, string[]>>;

export interface FeatureRoutingSettings extends LlmSettings {
  endpointCombos?: EndpointCombo[];
  featureRoutes?: FeatureRouteMap;
  evolutionRoutesSplit?: boolean;
  mainPreset?: MainPromptPreset;
  mainPresetProfileId?: string;
}

export const COMBO_KEY_PREFIX = "mh-rp-combo-key:";

export function comboKey(id: string) {
  return sessionStorage.getItem(`${COMBO_KEY_PREFIX}${id}`) ?? "";
}

function assignedCombos(
  featureId: FeatureId,
  settings: FeatureRoutingSettings,
  kind: EndpointKind,
) {
  const assignedIds = settings.featureRoutes?.[featureId];
  if (!assignedIds) return null;
  const byId = new Map(
    (settings.endpointCombos ?? []).map((combo) => [combo.id, combo]),
  );
  return assignedIds.flatMap((id) => {
    const combo = byId.get(id);
    return combo?.enabled && combo.kind === kind ? [combo] : [];
  });
}

export function resolveFeatureRoutes(
  featureId: FeatureId,
  settings: FeatureRoutingSettings,
): LlmRoute[] {
  const assigned = assignedCombos(featureId, settings, "generation");
  if (assigned)
    return assigned.map((combo) => ({
      id: `${featureId}:${combo.id}`,
      baseUrl: combo.baseUrl,
      model: combo.model,
      apiKey: comboKey(combo.id),
      useProxy: combo.useProxy,
      temperature: combo.temperature ?? settings.temperature,
      provider: combo.provider,
      apiStyle: combo.apiStyle,
      maxTokens: combo.maxTokens,
      timeoutMs: combo.timeoutMs,
      retries: combo.retries,
    }));
  return [{ ...settings, id: `${featureId}:legacy-default` }];
}

export function resolveEmbeddingRoute(
  settings: FeatureRoutingSettings,
): EmbeddingRoute | undefined {
  const assigned = assignedCombos("rag-embedding", settings, "embedding");
  if (assigned) {
    const combo = assigned[0];
    if (!combo) return undefined;
    return {
      baseUrl: combo.baseUrl,
      model: combo.model,
      apiKey: comboKey(combo.id),
      useProxy: combo.useProxy,
      dimensions: combo.dimensions,
      timeoutMs: combo.timeoutMs,
    };
  }
  return settings.embedding;
}

export function evolutionFeatureId(kind: string, splitRoutesEnabled = true): FeatureId {
  if (!splitRoutesEnabled) return "evolution";
  if (kind === "entry_all_focus") return "evolution-entry";
  if (kind === "item_management") return "evolution-item";
  if (kind === "player") return "evolution-player";
  if (kind === "npc") return "evolution-npc";
  if (kind === "monster") return "evolution-monster";
  return "evolution-misc";
}
